/**
 * 📋 SDLC 6.5.0 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/pages/platform-admin/TenantManagement.tsx
 * PURPOSE: Tenant lifecycle management with NQH Brand Design System
 * SDLC FRAMEWORK VERSION: 6.5.0 (7-Pillar + 9 Mental Models + Long-Running Agent Protocol)
 * PILLAR: P1 (Stage 06 - BUILD) + P6 (UI/UX Design)
 * SPRINT GOVERNANCE: Rule 2: Milestone-Gated Roadmaps
 *
 * CTO MANDATE: Production-ready React component with enterprise quality standards
 *
 * @business-context
 * - Domain: Multi-tenant Platform Administration, Vietnamese F&B
 * - Impact: Platform Critical - Tenant lifecycle management + tier/billing change
 * - Stakeholders: Platform Administrators, Nhat Quang Holding, IT Team
 *
 * @technical-architecture
 * - Layer: Presentation Layer, Page Components
 * - Pattern: React, CSS Modules, NQH Brand Design System
 * - Dependencies: React 18, React Router, TypeScript, CSS Modules
 *
 * @cross-references
 * - Design: docs/02-design/04-ui-ux-design/NQH-BRAND-DESIGN-SYSTEM.md
 * - Design: docs/02-design/04-ui-ux-design/WIREFRAME-PLATFORM-ADMIN.md (pa-03 row menu — "Đổi gói (tier)")
 * - BR: docs/01-planning/01-requirements/03-tenant-management/BR-TENM-010-BILLING-SUBSCRIPTION.md
 * - API: docs/03-integrate/01-API-Specifications/
 * - Backend: backend/app/api/v1/platform_admin.py (GET .../usage, GET .../downgrade-preview, PATCH .../tier)
 */

import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { usePlatformAdminAuth } from '../../contexts/PlatformAdminContext';
import {
  platformAdminApi,
  TenantResponse,
  TenantCreateRequest,
  TenantUsageResponse,
  TenantDowngradePreviewResponse,
} from '../../services/api/platform-admin/platformAdmin';
import styles from './TenantManagement.module.css';

// BR-TENM-010 §3.2 / backend platform_admin.py::change_tenant_tier() tier_order —
// keep in sync: used only to decide upgrade vs downgrade on the FE before deciding
// whether to call GET .../downgrade-preview.
const TIER_ORDER: Record<string, number> = {
  lite: 0,
  standard: 1,
  professional: 2,
  enterprise: 3,
};

export const TenantManagement: React.FC = () => {
  const { platformAdmin, isAuthenticated, isInitialized } = usePlatformAdminAuth();
  const navigate = useNavigate();
  const [tenants, setTenants] = useState<TenantResponse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [tierFilter, setTierFilter] = useState<string>('');

  // Reset Password Modal State
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [resetPasswordTenant, setResetPasswordTenant] = useState<TenantResponse | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [resetPasswordError, setResetPasswordError] = useState<string | null>(null);
  const [resetPasswordSuccess, setResetPasswordSuccess] = useState<string | null>(null);

  const [newTenantData, setNewTenantData] = useState<TenantCreateRequest>({
    name: '',
    admin_email: '',
    admin_password: '',
    admin_full_name: '',
    tier: 'lite',  // DD-0048 + CRP-2026-020: LITE (was STARTER)
    configuration: { operating_mode: 'standalone' },
    notes: '',
  });
  const [passwordError, setPasswordError] = useState<string | null>(null);

  // DD-0038 §2.1 v1.2: BFlow source assignment (FR-008)
  const [showBflowSourceModal, setShowBflowSourceModal] = useState(false);
  const [bflowSources, setBflowSources] = useState<Array<{tenant_id: string | null; label: string; shift_count: number}>>([]);
  const [bflowTargetTenant, setBflowTargetTenant] = useState<TenantResponse | null>(null);
  const [selectedBflowSource, setSelectedBflowSource] = useState<string | null>(null);

  // S106R-002: Mode Transition Modal State (DD-0005 §5.2)
  const [showModeModal, setShowModeModal] = useState(false);
  const [modeTargetTenant, setModeTargetTenant] = useState<TenantResponse | null>(null);
  const [selectedMode, setSelectedMode] = useState('');
  const [erpProvider, setErpProvider] = useState('');
  const [reason, setReason] = useState('');
  const [modeError, setModeError] = useState<string | null>(null);
  const [modeSuccess, setModeSuccess] = useState<string | null>(null);
  const [isSubmittingMode, setIsSubmittingMode] = useState(false);

  // BR-TENM-010: Tier Change Modal State ("Đổi gói" per pa-03 row menu)
  const [showTierModal, setShowTierModal] = useState(false);
  const [tierTargetTenant, setTierTargetTenant] = useState<TenantResponse | null>(null);
  const [tierUsage, setTierUsage] = useState<TenantUsageResponse | null>(null);
  const [isLoadingTierUsage, setIsLoadingTierUsage] = useState(false);
  const [selectedNewTier, setSelectedNewTier] = useState('');
  const [tierReason, setTierReason] = useState('');
  const [downgradePreview, setDowngradePreview] = useState<TenantDowngradePreviewResponse | null>(null);
  const [isLoadingDowngradePreview, setIsLoadingDowngradePreview] = useState(false);
  const [tierError, setTierError] = useState<string | null>(null);
  const [tierSuccess, setTierSuccess] = useState<string | null>(null);
  const [isSubmittingTier, setIsSubmittingTier] = useState(false);

  // Redirect to PA login if not authenticated after initialization
  useEffect(() => {
    if (isInitialized && !isAuthenticated) {
      navigate('/platform-admin/login');
    }
  }, [isInitialized, isAuthenticated, navigate]);

  useEffect(() => {
    loadTenants();
  }, [statusFilter, tierFilter, platformAdmin.accessToken]);

  const loadTenants = async () => {
    if (!platformAdmin.accessToken) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const tenantList = await platformAdminApi.listTenants(platformAdmin.accessToken, {
        status_filter: statusFilter || undefined,
        tier_filter: tierFilter || undefined,
        limit: 1000,
      });

      setTenants(tenantList);
    } catch (err: any) {
      setError(err.message || 'Failed to load tenants');
    } finally {
      setIsLoading(false);
    }
  };

  // Password validation per auth-register.yaml: 8+ chars, number, uppercase, lowercase
  const validatePassword = (password: string): string | null => {
    if (password.length < 8) {
      return 'Password must be at least 8 characters';
    }
    if (!/[0-9]/.test(password)) {
      return 'Password must contain at least 1 number';
    }
    if (!/[A-Z]/.test(password)) {
      return 'Password must contain at least 1 uppercase letter';
    }
    if (!/[a-z]/.test(password)) {
      return 'Password must contain at least 1 lowercase letter';
    }
    return null;
  };

  const handleCreateTenant = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!platformAdmin.accessToken) return;

    // Validate password before submission
    const pwdError = validatePassword(newTenantData.admin_password);
    if (pwdError) {
      setPasswordError(pwdError);
      return;
    }
    setPasswordError(null);

    try {
      setIsCreating(true);
      setError(null);

      const createdTenant = await platformAdminApi.createTenant(platformAdmin.accessToken, newTenantData);

      // Add to list
      setTenants(prev => [createdTenant, ...prev]);

      // Reset form
      setNewTenantData({
        name: '',
        admin_email: '',
        admin_password: '',
        admin_full_name: '',
        tier: 'lite',  // DD-0048 + CRP-2026-020: LITE (was STARTER)
        configuration: { operating_mode: 'standalone' },
        notes: '',
      });
      setPasswordError(null);

      setShowCreateModal(false);
    } catch (err: any) {
      setError(err.message || 'Failed to create tenant');
    } finally {
      setIsCreating(false);
    }
  };

  const handleUpdateTenantStatus = async (tenantKey: string, newStatus: string) => {
    if (!platformAdmin.accessToken) return;
    if (!confirm(`Are you sure you want to change tenant status to ${newStatus}?`)) return;

    try {
      await platformAdminApi.updateTenantStatus(platformAdmin.accessToken, tenantKey, newStatus);

      // Update local state
      setTenants(prev => prev.map(tenant =>
        tenant.tenant_key === tenantKey ? { ...tenant, status: newStatus } : tenant
      ));
    } catch (err: any) {
      setError(err.message || 'Failed to update tenant status');
    }
  };

  // Open reset password modal for a specific tenant
  const openResetPasswordModal = (tenant: TenantResponse) => {
    setResetPasswordTenant(tenant);
    setNewPassword('');
    setConfirmPassword('');
    setResetPasswordError(null);
    setResetPasswordSuccess(null);
    setShowResetPasswordModal(true);
  };

  // Handle reset password submission
  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!platformAdmin.accessToken || !resetPasswordTenant) return;

    // Validate passwords match
    if (newPassword !== confirmPassword) {
      setResetPasswordError('Passwords do not match');
      return;
    }

    // Validate password strength
    const pwdError = validatePassword(newPassword);
    if (pwdError) {
      setResetPasswordError(pwdError);
      return;
    }

    try {
      setIsResettingPassword(true);
      setResetPasswordError(null);

      const result = await platformAdminApi.resetTenantAdminPassword(
        platformAdmin.accessToken,
        resetPasswordTenant.tenant_key,
        newPassword
      );

      setResetPasswordSuccess(`Password reset successfully for ${result.admin_email}`);
      setNewPassword('');
      setConfirmPassword('');

      // Close modal after 2 seconds
      setTimeout(() => {
        setShowResetPasswordModal(false);
        setResetPasswordTenant(null);
        setResetPasswordSuccess(null);
      }, 2000);

    } catch (err: any) {
      setResetPasswordError(err.message || 'Failed to reset password');
    } finally {
      setIsResettingPassword(false);
    }
  };

  const getStatusBadgeClass = (status: string): string => {
    switch (status.toLowerCase()) {
      case 'active': return styles.badgeActive;
      case 'suspended': return styles.badgeSuspended;
      case 'provisioning': return styles.badgeProvisioning;
      case 'pending': return styles.badgePending;
      default: return styles.badgePending;
    }
  };

  // DD-0048 + @researcher Option 3 Hybrid: Vietnamese labels for UI
  // API values: lite / standard / professional / enterprise (DD-0048 §4.1)
  // Vietnamese labels: Khởi đầu / Tiêu chuẩn / Chuyên nghiệp / Doanh nghiệp
  // Evidence: 7 Vietnamese SaaS platforms surveyed. "Chuyên nghiệp" + "Doanh nghiệp" = 100% consensus.
  const getTierLabel = (tier: string): string => {
    switch (tier.toLowerCase()) {
      case 'lite': return 'Khởi đầu';
      case 'standard': return 'Tiêu chuẩn';
      case 'professional': return 'Chuyên nghiệp';
      case 'enterprise': return 'Doanh nghiệp';
      // ERRATA: backward compat for existing data before DD-0048 migration
      case 'starter': return 'Khởi đầu';
      case 'custom': return 'Tùy chỉnh';
      default: return tier.toUpperCase();
    }
  };

  const getTierBadgeClass = (tier: string): string => {
    switch (tier.toLowerCase()) {
      case 'enterprise': return styles.badgeEnterprise;
      case 'professional': return styles.badgeProfessional;
      case 'lite': return styles.badgeStarter;       // reuse starter style for lite
      case 'standard': return styles.badgeProfessional; // use professional style for standard
      case 'starter': return styles.badgeStarter;    // backward compat
      default: return styles.badgePending;
    }
  };

  // ── S106R-002: Mode transition helpers (DD-0005 §5.2) ──
  const getCurrentMode = (tenant: TenantResponse): string => {
    return (tenant as any).configuration?.operating_mode || 'standalone';
  };

  const ALLOWED_TRANSITIONS: Record<string, string[]> = {
    standalone: ['erp_connected', 'nqh_ecosystem'],
    erp_connected: ['nqh_ecosystem', 'standalone'],
    nqh_ecosystem: ['erp_connected', 'standalone'],
  };

  const MODE_LABELS: Record<string, string> = {
    standalone: 'Độc lập (Standalone)',
    erp_connected: 'Kết nối ERP (MISA/Odoo/SAP)',
    nqh_ecosystem: 'Hệ sinh thái NQH (BFlow)',
  };

  const getSideEffectWarning = (fromMode: string, toMode: string, bflowSyncEnabled: boolean): string | null => {
    if (toMode === 'standalone') {
      if (bflowSyncEnabled) {
        return 'Chuyển sang chế độ Độc lập sẽ tự động tắt BFlow Sync (bflow_sync_enabled = false).';
      }
      return 'Chuyển sang chế độ Độc lập sẽ xóa cấu hình ERP provider.';
    }
    if (fromMode === 'erp_connected' && toMode === 'nqh_ecosystem') {
      return 'Dữ liệu ERP hiện tại sẽ được thay thế bởi BFlow. Đảm bảo di chuyển dữ liệu ERP trước khi tiếp tục.';
    }
    if (fromMode === 'nqh_ecosystem' && toMode === 'erp_connected') {
      return 'Dữ liệu Hệ sinh thái NQH sẽ ngừng đồng bộ. Đảm bảo di chuyển dữ liệu BFlow trước khi tiếp tục.';
    }
    return null;
  };

  const openModeModal = (tenant: TenantResponse) => {
    setModeTargetTenant(tenant);
    setSelectedMode('');
    setErpProvider('');
    setReason('');
    setModeError(null);
    setModeSuccess(null);
    setIsSubmittingMode(false);
    setShowModeModal(true);
  };

  const closeModeModal = () => {
    setShowModeModal(false);
    setModeTargetTenant(null);
  };

  const handleUpdateMode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!platformAdmin.accessToken || !modeTargetTenant) return;

    if (!selectedMode) {
      setModeError('Vui lòng chọn chế độ vận hành mới');
      return;
    }
    if (!reason || reason.trim().length < 10) {
      setModeError('Lý do phải có ít nhất 10 ký tự');
      return;
    }

    const currentMode = getCurrentMode(modeTargetTenant);
    if (currentMode === selectedMode) {
      setModeError('Chế độ mới phải khác chế độ hiện tại');
      return;
    }

    setIsSubmittingMode(true);
    setModeError(null);

    try {
      const payload: { operating_mode: string; reason: string; new_erp_provider?: string } = {
        operating_mode: selectedMode,
        reason: reason.trim(),
      };
      if (selectedMode === 'erp_connected' && erpProvider) {
        payload.new_erp_provider = erpProvider;
      }

      await platformAdminApi.updateTenantOperatingMode(
        platformAdmin.accessToken,
        modeTargetTenant.tenant_key,
        payload
      );

      setModeSuccess('Cập nhật chế độ vận hành thành công');
      await loadTenants();
      setTimeout(() => closeModeModal(), 1500);
    } catch (err: any) {
      setModeError(err.message || 'Cập nhật chế độ vận hành thất bại');
    } finally {
      setIsSubmittingMode(false);
    }
  };

  // ── BR-TENM-010: Tier change helpers ("Đổi gói") ──
  const openTierModal = async (tenant: TenantResponse) => {
    setTierTargetTenant(tenant);
    setSelectedNewTier('');
    setTierReason('');
    setDowngradePreview(null);
    setTierError(null);
    setTierSuccess(null);
    setIsSubmittingTier(false);
    setTierUsage(null);
    setShowTierModal(true);

    if (!platformAdmin.accessToken) return;

    try {
      setIsLoadingTierUsage(true);
      const usage = await platformAdminApi.getTenantUsage(platformAdmin.accessToken, tenant.tenant_key);
      setTierUsage(usage);
    } catch (err: any) {
      setTierError(err.message || 'Không thể tải thông tin sử dụng gói.');
    } finally {
      setIsLoadingTierUsage(false);
    }
  };

  const closeTierModal = () => {
    setShowTierModal(false);
    setTierTargetTenant(null);
  };

  const isDowngradeSelected = !!(
    tierTargetTenant &&
    selectedNewTier &&
    (TIER_ORDER[selectedNewTier] ?? 0) < (TIER_ORDER[tierTargetTenant.tier] ?? 0)
  );

  const handleSelectNewTier = async (tier: string) => {
    setSelectedNewTier(tier);
    setDowngradePreview(null);
    setTierError(null);

    if (!tierTargetTenant || !platformAdmin.accessToken || !tier) return;

    const isDowngrade = (TIER_ORDER[tier] ?? 0) < (TIER_ORDER[tierTargetTenant.tier] ?? 0);
    if (!isDowngrade) return;

    try {
      setIsLoadingDowngradePreview(true);
      const preview = await platformAdminApi.previewTenantDowngrade(
        platformAdmin.accessToken,
        tierTargetTenant.tenant_key,
        tier
      );
      setDowngradePreview(preview);
    } catch (err: any) {
      setTierError(err.message || 'Không thể tải xem trước hạ gói.');
    } finally {
      setIsLoadingDowngradePreview(false);
    }
  };

  const handleConfirmTierChange = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!platformAdmin.accessToken || !tierTargetTenant || !selectedNewTier) return;

    setIsSubmittingTier(true);
    setTierError(null);

    try {
      const result = await platformAdminApi.changeTenantTier(
        platformAdmin.accessToken,
        tierTargetTenant.tenant_key,
        {
          new_tier: selectedNewTier,
          reason: tierReason.trim() || undefined,
        }
      );

      setTierSuccess(
        `Đổi gói thành công: ${getTierLabel(result.previous_tier)} → ${getTierLabel(result.new_tier)}`
      );

      // Refresh tenant's displayed tier in the table — no full page reload.
      setTenants(prev => prev.map(t =>
        t.tenant_key === tierTargetTenant.tenant_key ? { ...t, tier: result.new_tier } : t
      ));

      // Best-effort refresh of the usage panel while the modal's success message shows.
      try {
        const usage = await platformAdminApi.getTenantUsage(
          platformAdmin.accessToken,
          tierTargetTenant.tenant_key
        );
        setTierUsage(usage);
      } catch {
        // Non-fatal — the tier change itself already succeeded.
      }

      setTimeout(() => closeTierModal(), 1500);
    } catch (err: any) {
      if (err.statusCode === 403) {
        setTierError('Bạn không có đủ quyền để đổi gói tenant (yêu cầu vai trò Vận hành nền tảng trở lên).');
      } else {
        setTierError(err.message || 'Đổi gói thất bại. Vui lòng thử lại.');
      }
    } finally {
      setIsSubmittingTier(false);
    }
  };

  if (isLoading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.spinner}></div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      {/* Header */}
      <header className={styles.header}>
        <div className={styles.headerContainer}>
          <div className={styles.headerContent}>
            <div>
              <nav className={styles.breadcrumb} aria-label="Breadcrumb">
                <ol className={styles.breadcrumbList}>
                  <li>
                    <Link to="/platform-admin/dashboard" className={styles.breadcrumbLink}>
                      Bảng điều khiển
                    </Link>
                  </li>
                  <li>
                    <span className={styles.breadcrumbSeparator}>/</span>
                  </li>
                  <li>
                    <span className={styles.breadcrumbCurrent}>Quản lý Tenant</span>
                  </li>
                </ol>
              </nav>
              <h1 className={styles.pageTitle}>Quản lý Tenant</h1>
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              className={styles.primaryButton}
            >
              Tạo Tenant mới
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className={styles.main}>
        {error && (
          <div className={styles.errorAlert}>
            {error}
            <button
              onClick={() => setError(null)}
              className={styles.errorDismiss}
            >
              ×
            </button>
          </div>
        )}

        {/* Filters */}
        <div className={styles.card}>
          <div className={styles.cardContent}>
            <h3 className={styles.cardTitle}>Bộ lọc</h3>
            <div className={styles.filtersGrid}>
              <div className={styles.filterGroup}>
                <label>Trạng thái</label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className={styles.filterSelect}
                >
                  <option value="">Tất cả trạng thái</option>
                  <option value="active">Hoạt động</option>
                  <option value="suspended">Tạm ngưng</option>
                  <option value="provisioning">Đang khởi tạo</option>
                  <option value="pending">Chờ xử lý</option>
                </select>
              </div>

              <div className={styles.filterGroup}>
                <label>Gói dịch vụ</label>
                <select
                  value={tierFilter}
                  onChange={(e) => setTierFilter(e.target.value)}
                  className={styles.filterSelect}
                >
                  <option value="">Tất cả gói</option>
                  <option value="lite">Khởi đầu</option>
                  <option value="standard">Tiêu chuẩn</option>
                  <option value="professional">Chuyên nghiệp</option>
                  <option value="enterprise">Doanh nghiệp</option>
                </select>
              </div>

              <div className={styles.filterButtonContainer}>
                <button
                  onClick={loadTenants}
                  className={styles.secondaryButton}
                >
                  Làm mới
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Tenants Table */}
        <div className={styles.card}>
          <div className={styles.cardContent}>
            <h3 className={styles.cardTitle}>
              Danh sách Tenant ({tenants.length})
            </h3>

            <div className={styles.tableContainer}>
              <table className={styles.table}>
                <thead className={styles.tableHead}>
                  <tr>
                    <th className={styles.tableHeader}>
                      Thông tin Tenant
                    </th>
                    <th className={styles.tableHeader}>
                      Quản trị
                    </th>
                    <th className={styles.tableHeader}>
                      Trạng thái & Gói
                    </th>
                    <th className={styles.tableHeader}>
                      Ngày tạo
                    </th>
                    <th className={styles.tableHeader}>
                      Hành động
                    </th>
                  </tr>
                </thead>
                <tbody className={styles.tableBody}>
                  {tenants.map((tenant) => (
                    <tr key={tenant.id} className={styles.tableRow}>
                      <td className={styles.tableCell}>
                        <div>
                          <div className={styles.tenantName}>{tenant.name}</div>
                          <div className={styles.tenantKey}>{tenant.tenant_key}</div>
                          <div className={styles.tenantSchema}>Schema: {tenant.schema_name}</div>
                        </div>
                      </td>
                      <td className={styles.tableCell}>
                        <div className={styles.tenantEmail}>{tenant.admin_email}</div>
                      </td>
                      <td className={styles.tableCell}>
                        <div className={styles.badgeContainer}>
                          <span className={`${styles.badge} ${getStatusBadgeClass(tenant.status)}`}>
                            {tenant.status.toUpperCase()}
                          </span>
                          <span className={`${styles.badge} ${getTierBadgeClass(tenant.tier)}`}>
                            {getTierLabel(tenant.tier)}
                          </span>
                        </div>
                      </td>
                      <td className={styles.tableCell}>
                        <span className={styles.dateCell}>
                          {new Date(tenant.created_at).toLocaleDateString()}
                        </span>
                      </td>
                      <td className={styles.tableCell}>
                        <div className={styles.actionsContainer}>
                          {tenant.status === 'active' && (
                            <button
                              onClick={() => handleUpdateTenantStatus(tenant.tenant_key, 'suspended')}
                              className={`${styles.actionButton} ${styles.actionSuspend}`}
                            >
                              Tạm ngưng
                            </button>
                          )}
                          {tenant.status === 'suspended' && (
                            <button
                              onClick={() => handleUpdateTenantStatus(tenant.tenant_key, 'active')}
                              className={`${styles.actionButton} ${styles.actionActivate}`}
                            >
                              Kích hoạt
                            </button>
                          )}
                          {tenant.status === 'provisioning' && (
                            <button
                              onClick={() => handleUpdateTenantStatus(tenant.tenant_key, 'active')}
                              className={`${styles.actionButton} ${styles.actionInfo}`}
                            >
                              Kích hoạt
                            </button>
                          )}
                          {/* DD-0038 §2.1 v1.2: BFlow Sync Toggle + Source Assignment (FR-008) */}
                          <button
                            onClick={async () => {
                              const current = tenant.configuration?.bflow_sync_enabled || false;
                              if (current) {
                                // Tắt — direct toggle, no source needed
                                try {
                                  await platformAdminApi.updateTenantFeatures(
                                    platformAdmin.accessToken!,
                                    tenant.tenant_key,
                                    { bflow_sync_enabled: false }
                                  );
                                  await loadTenants();
                                } catch (err: any) {
                                  setError(`BFlow toggle failed: ${err.message || 'Unknown error'}`);
                                }
                              } else {
                                // Bật — load sources, show modal (FR-008a)
                                try {
                                  const data = await platformAdminApi.listBflowSources(platformAdmin.accessToken!);
                                  setBflowSources(data.sources || []);
                                  setBflowTargetTenant(tenant);
                                  setSelectedBflowSource(tenant.configuration?.bflow_source_tenant_id ?? '__null__');
                                  setShowBflowSourceModal(true);
                                } catch (err: any) {
                                  setError(`Load BFlow sources failed: ${err.message || 'Unknown error'}`);
                                }
                              }
                            }}
                            className={`${styles.actionButton} ${tenant.configuration?.bflow_sync_enabled ? styles.actionSuspend : styles.actionActivate}`}
                            title={tenant.configuration?.bflow_sync_enabled ? "Tắt BFlow Sync" : "Bật BFlow Sync — chọn nguồn dữ liệu"}
                          >
                            {tenant.configuration?.bflow_sync_enabled ? "BFlow: Bật" : "BFlow: Tắt"}
                          </button>
                          {/* S106R-002: Edit Mode Button (DD-0005 §5.2) */}
                          <button
                            onClick={() => openModeModal(tenant)}
                            className={`${styles.actionButton} ${styles.actionInfo}`}
                            title="Chỉnh sửa chế độ vận hành"
                          >
                            Chỉnh sửa chế độ
                          </button>
                          {/* BR-TENM-010: Change Tier Button (pa-03 row menu — "Đổi gói (tier)") */}
                          <button
                            onClick={() => openTierModal(tenant)}
                            className={`${styles.actionButton} ${styles.actionInfo}`}
                            title="Đổi gói dịch vụ tenant"
                          >
                            Đổi gói
                          </button>
                          {/* Reset Password Button - Available for all tenants */}
                          <button
                            onClick={() => openResetPasswordModal(tenant)}
                            className={`${styles.actionButton} ${styles.actionResetPassword}`}
                            title="Đặt lại mật khẩu quản trị tenant"
                          >
                            Đặt lại mật khẩu
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {tenants.length === 0 && (
                <div className={styles.emptyState}>
                  Không tìm thấy tenant nào. Tạo tenant đầu tiên để bắt đầu.
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Create Tenant Modal */}
      {showCreateModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <h3 className={styles.modalTitle}>Tạo Tenant mới</h3>

            <form onSubmit={handleCreateTenant} className={styles.form}>
              <div className={styles.formGroup}>
                <label>Tên Tenant</label>
                <input
                  type="text"
                  required
                  value={newTenantData.name}
                  onChange={(e) => setNewTenantData(prev => ({ ...prev, name: e.target.value }))}
                  className={styles.formInput}
                />
              </div>

              <div className={styles.formGroup}>
                <label>Email quản trị</label>
                <input
                  type="email"
                  required
                  value={newTenantData.admin_email}
                  onChange={(e) => setNewTenantData(prev => ({ ...prev, admin_email: e.target.value }))}
                  className={styles.formInput}
                />
              </div>

              <div className={styles.formGroup}>
                <label>Mật khẩu quản trị *</label>
                <input
                  type="password"
                  required
                  minLength={8}
                  value={newTenantData.admin_password}
                  onChange={(e) => {
                    setNewTenantData(prev => ({ ...prev, admin_password: e.target.value }));
                    setPasswordError(null);
                  }}
                  className={`${styles.formInput} ${passwordError ? styles.formInputError : ''}`}
                  placeholder="Tối thiểu 8 ký tự, 1 số, 1 chữ hoa, 1 chữ thường"
                />
                {passwordError && (
                  <span className={styles.fieldError}>{passwordError}</span>
                )}
              </div>

              <div className={styles.formGroup}>
                <label>Họ và tên quản trị</label>
                <input
                  type="text"
                  value={newTenantData.admin_full_name || ''}
                  onChange={(e) => setNewTenantData(prev => ({ ...prev, admin_full_name: e.target.value }))}
                  className={styles.formInput}
                  placeholder="Tùy chọn - mặc định là '{Tenant Name} Admin'"
                />
              </div>

              <div className={styles.formGroup}>
                <label>Gói dịch vụ</label>
                <select
                  value={newTenantData.tier}
                  onChange={(e) => setNewTenantData(prev => ({ ...prev, tier: e.target.value }))}
                  className={styles.formSelect}
                >
                  <option value="lite">Khởi đầu</option>
                  <option value="standard">Tiêu chuẩn</option>
                  <option value="professional">Chuyên nghiệp</option>
                  <option value="enterprise">Doanh nghiệp</option>
                </select>
              </div>

              {/* DD-0005: Operating Mode — architectural linchpin for multi-industry BPM */}
              <div className={styles.formGroup}>
                <label>Chế độ vận hành</label>
                <select
                  value={newTenantData.configuration?.operating_mode || 'standalone'}
                  onChange={(e) => {
                    const mode = e.target.value;
                    setNewTenantData(prev => ({
                      ...prev,
                      configuration: {
                        ...prev.configuration,
                        operating_mode: mode,
                        // Clear erp_provider when switching to standalone
                        ...(mode === 'standalone' ? { erp_provider: undefined } : {}),
                      },
                    }));
                  }}
                  className={styles.formSelect}
                >
                  <option value="standalone">Độc lập (Standalone)</option>
                  <option value="erp_connected">Kết nối ERP (MISA/Odoo/SAP)</option>
                  <option value="nqh_ecosystem">Hệ sinh thái NQH (BFlow)</option>
                </select>
              </div>

              {/* DD-0005 §5: erp_provider required when mode ≠ standalone */}
              {newTenantData.configuration?.operating_mode && newTenantData.configuration.operating_mode !== 'standalone' && (
                <div className={styles.formGroup}>
                  <label>Nhà cung cấp ERP {newTenantData.configuration.operating_mode === 'nqh_ecosystem' ? '' : '*'}</label>
                  <select
                    value={newTenantData.configuration?.erp_provider || (newTenantData.configuration.operating_mode === 'nqh_ecosystem' ? 'bflow' : '')}
                    onChange={(e) => setNewTenantData(prev => ({
                      ...prev,
                      configuration: { ...prev.configuration, erp_provider: e.target.value },
                    }))}
                    className={styles.formSelect}
                    required={newTenantData.configuration.operating_mode === 'erp_connected'}
                  >
                    {newTenantData.configuration.operating_mode === 'nqh_ecosystem' ? (
                      <option value="bflow">BFlow ERP</option>
                    ) : (
                      <>
                        <option value="">-- Chọn nhà cung cấp --</option>
                        <option value="misa">MISA AMIS</option>
                        <option value="odoo">Odoo</option>
                        <option value="sap">SAP</option>
                      </>
                    )}
                  </select>
                </div>
              )}

              <div className={styles.formGroup}>
                <label>Ghi chú</label>
                <textarea
                  value={newTenantData.notes}
                  onChange={(e) => setNewTenantData(prev => ({ ...prev, notes: e.target.value }))}
                  rows={3}
                  className={styles.formTextarea}
                />
              </div>

              <div className={styles.modalActions}>
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className={styles.cancelButton}
                >
                  Hủy
                </button>
                <button
                  type="submit"
                  disabled={isCreating}
                  className={styles.primaryButton}
                >
                  {isCreating ? 'Đang tạo...' : 'Tạo Tenant'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {showResetPasswordModal && resetPasswordTenant && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <h3 className={styles.modalTitle}>Đặt lại mật khẩu quản trị Tenant</h3>

            <div className={styles.resetPasswordInfo}>
              <p><strong>Tenant:</strong> {resetPasswordTenant.name}</p>
              <p><strong>Email quản trị:</strong> {resetPasswordTenant.admin_email}</p>
            </div>

            {resetPasswordSuccess && (
              <div className={styles.successAlert}>
                {resetPasswordSuccess}
              </div>
            )}

            {resetPasswordError && (
              <div className={styles.errorAlertInline}>
                {resetPasswordError}
              </div>
            )}

            <form onSubmit={handleResetPassword} className={styles.form}>
              <div className={styles.formGroup}>
                <label>Mật khẩu mới *</label>
                <input
                  type="password"
                  required
                  minLength={8}
                  value={newPassword}
                  onChange={(e) => {
                    setNewPassword(e.target.value);
                    setResetPasswordError(null);
                  }}
                  className={styles.formInput}
                  placeholder="Tối thiểu 8 ký tự, 1 số, 1 chữ hoa, 1 chữ thường"
                  disabled={!!resetPasswordSuccess}
                />
              </div>

              <div className={styles.formGroup}>
                <label>Xác nhận mật khẩu *</label>
                <input
                  type="password"
                  required
                  minLength={8}
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    setResetPasswordError(null);
                  }}
                  className={styles.formInput}
                  placeholder="Nhập lại mật khẩu mới"
                  disabled={!!resetPasswordSuccess}
                />
              </div>

              <div className={styles.modalActions}>
                <button
                  type="button"
                  onClick={() => {
                    setShowResetPasswordModal(false);
                    setResetPasswordTenant(null);
                  }}
                  className={styles.cancelButton}
                  disabled={isResettingPassword}
                >
                  Hủy
                </button>
                <button
                  type="submit"
                  disabled={isResettingPassword || !!resetPasswordSuccess}
                  className={styles.primaryButton}
                >
                  {isResettingPassword ? 'Đang đặt lại...' : 'Đặt lại mật khẩu'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* DD-0038 §2.1 v1.2: BFlow Source Assignment Modal (FR-008a) */}
      {showBflowSourceModal && bflowTargetTenant && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <h3 className={styles.modalTitle}>Chọn nguồn BFlow data</h3>
            <p style={{ margin: '8px 0 16px', color: '#6B7280' }}>
              Tenant: <strong>{bflowTargetTenant.name}</strong> ({bflowTargetTenant.tenant_key})
            </p>

            {bflowSources.length === 0 ? (
              <p style={{ color: '#EF4444' }}>Không có nguồn BFlow data nào. Hãy push data từ BFlow ERP trước.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {bflowSources.map((src) => {
                  const key = src.tenant_id ?? '__null__';
                  return (
                    <label key={key} style={{
                      display: 'flex', alignItems: 'center', gap: '8px',
                      padding: '10px 12px', border: '1px solid #E5E7EB', borderRadius: '6px',
                      cursor: 'pointer',
                      background: selectedBflowSource === key ? '#FFF7ED' : '#fff',
                      borderColor: selectedBflowSource === key ? '#F97316' : '#E5E7EB',
                    }}>
                      <input
                        type="radio"
                        name="bflow-source"
                        value={key}
                        checked={selectedBflowSource === key}
                        onChange={() => setSelectedBflowSource(key)}
                      />
                      <span><strong>{src.label}</strong> ({src.shift_count} mã ca)</span>
                    </label>
                  );
                })}
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px' }}>
              <button
                onClick={() => setShowBflowSourceModal(false)}
                className={styles.secondaryButton}
              >
                Hủy
              </button>
              <button
                disabled={bflowSources.length === 0}
                onClick={async () => {
                  try {
                    const sourceValue = selectedBflowSource === '__null__' ? null : selectedBflowSource;
                    await platformAdminApi.updateTenantFeatures(
                      platformAdmin.accessToken!,
                      bflowTargetTenant.tenant_key,
                      { bflow_sync_enabled: true, bflow_source_tenant_id: sourceValue }
                    );
                    setShowBflowSourceModal(false);
                    await loadTenants();
                  } catch (err: any) {
                    setError(`BFlow enable failed: ${err.message || 'Unknown error'}`);
                  }
                }}
                className={styles.primaryButton}
              >
                Bật BFlow Sync
              </button>
            </div>
          </div>
        </div>
      )}

      {/* S106R-002: Mode Transition Modal (DD-0005 §5.2) */}
      {showModeModal && modeTargetTenant && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <h3 className={styles.modalTitle}>Chỉnh sửa chế độ vận hành</h3>
            <p style={{ margin: '8px 0 16px', color: '#6B7280' }}>
              Tenant: <strong>{modeTargetTenant.name}</strong> ({modeTargetTenant.tenant_key})
            </p>
            <p style={{ margin: '4px 0 16px', color: '#6B7280' }}>
              Chế độ hiện tại: <strong>{MODE_LABELS[getCurrentMode(modeTargetTenant)]}</strong>
            </p>

            {modeSuccess && (
              <div className={styles.successAlert}>{modeSuccess}</div>
            )}
            {modeError && (
              <div className={styles.errorAlertInline}>{modeError}</div>
            )}

            <form onSubmit={handleUpdateMode} className={styles.form}>
              {/* Mode dropdown — filtered to allowed transitions only */}
              <div className={styles.formGroup}>
                <label htmlFor="mode-select">Chế độ vận hành mới *</label>
                <select
                  id="mode-select"
                  value={selectedMode}
                  onChange={(e) => {
                    setSelectedMode(e.target.value);
                    setErpProvider('');
                    setModeError(null);
                  }}
                  className={styles.formSelect}
                  required
                >
                  <option value="">-- Chọn chế độ --</option>
                  {ALLOWED_TRANSITIONS[getCurrentMode(modeTargetTenant)]?.map((mode) => (
                    <option key={mode} value={mode}>{MODE_LABELS[mode]}</option>
                  ))}
                </select>
              </div>

              {/* ERP provider — conditional, only for erp_connected target */}
              {selectedMode === 'erp_connected' && (
                <div className={styles.formGroup}>
                  <label htmlFor="erp-provider-select">Nhà cung cấp ERP *</label>
                  <select
                    id="erp-provider-select"
                    value={erpProvider}
                    onChange={(e) => setErpProvider(e.target.value)}
                    className={styles.formSelect}
                    required
                  >
                    <option value="">-- Chọn nhà cung cấp --</option>
                    <option value="misa">MISA AMIS</option>
                    <option value="odoo">Odoo</option>
                    <option value="sap">SAP</option>
                  </select>
                </div>
              )}

              {/* Reason textarea — audit requirement (BR-PADM-007 AC-006) */}
              <div className={styles.formGroup}>
                <label htmlFor="mode-reason">Lý do thay đổi * (tối thiểu 10 ký tự)</label>
                <textarea
                  id="mode-reason"
                  value={reason}
                  onChange={(e) => {
                    setReason(e.target.value);
                    setModeError(null);
                  }}
                  rows={3}
                  className={styles.formTextarea}
                  placeholder="Nhập lý do chuyển đổi chế độ vận hành..."
                  required
                  minLength={10}
                />
              </div>

              {/* Side-effect warning banner */}
              {selectedMode && (
                (() => {
                  const warning = getSideEffectWarning(
                    getCurrentMode(modeTargetTenant),
                    selectedMode,
                    !!(modeTargetTenant as any).configuration?.bflow_sync_enabled
                  );
                  return warning ? (
                    <div
                      style={{
                        background: '#FEF3C7',
                        border: '1px solid #F59E0B',
                        borderRadius: '6px',
                        padding: '12px',
                        marginBottom: '16px',
                        color: '#92400E',
                        fontSize: '14px',
                      }}
                      data-testid="mode-side-effect-warning"
                    >
                      <strong>⚠ Cảnh báo:</strong> {warning}
                    </div>
                  ) : null;
                })()
              )}

              {/* Audit-event preview */}
              {selectedMode && reason.trim().length >= 10 && (
                <div
                  style={{
                    background: '#EFF6FF',
                    border: '1px solid #3B82F6',
                    borderRadius: '6px',
                    padding: '12px',
                    marginBottom: '16px',
                    color: '#1E40AF',
                    fontSize: '13px',
                  }}
                  data-testid="mode-audit-preview"
                >
                  <strong>Xem trước bản ghi audit:</strong>
                  <ul style={{ margin: '6px 0 0', paddingLeft: '18px' }}>
                    <li>Người thực hiện: {platformAdmin.email || 'N/A'}</li>
                    <li>Trước: {MODE_LABELS[getCurrentMode(modeTargetTenant)]}</li>
                    <li>Sau: {MODE_LABELS[selectedMode]}</li>
                    <li>Lý do: {reason.trim()}</li>
                  </ul>
                </div>
              )}

              <div className={styles.modalActions}>
                <button
                  type="button"
                  onClick={closeModeModal}
                  className={styles.cancelButton}
                  disabled={isSubmittingMode}
                >
                  Hủy
                </button>
                <button
                  type="submit"
                  disabled={isSubmittingMode || !!modeSuccess}
                  className={styles.primaryButton}
                >
                  {isSubmittingMode ? 'Đang cập nhật...' : 'Xác nhận thay đổi'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* BR-TENM-010: Change Tier Modal ("Đổi gói") */}
      {showTierModal && tierTargetTenant && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <h3 className={styles.modalTitle}>Đổi gói dịch vụ</h3>
            <p style={{ margin: '8px 0 16px', color: '#6B7280' }}>
              Tenant: <strong>{tierTargetTenant.name}</strong> ({tierTargetTenant.tenant_key})
            </p>

            {tierSuccess && (
              <div className={styles.successAlert}>{tierSuccess}</div>
            )}
            {tierError && (
              <div className={styles.errorAlertInline}>{tierError}</div>
            )}

            {/* Current tier + usage vs limits (GET .../usage) */}
            {isLoadingTierUsage ? (
              <p style={{ color: '#6B7280' }}>Đang tải thông tin sử dụng...</p>
            ) : tierUsage ? (
              <div className={styles.resetPasswordInfo} data-testid="tier-usage-panel">
                <p><strong>Gói hiện tại:</strong> {tierUsage.tier_display}</p>
                <p>
                  <strong>Người dùng:</strong>{' '}
                  {tierUsage.usage.users.unlimited
                    ? `${tierUsage.usage.users.current}/∞`
                    : `${tierUsage.usage.users.current}/${tierUsage.usage.users.max} (${tierUsage.usage.users.percentage}%)`}
                </p>
                {tierUsage.warnings.map((w, idx) => (
                  <p key={idx} style={{ color: '#B45309' }}>⚠ {w.message}</p>
                ))}
              </div>
            ) : null}

            <form onSubmit={handleConfirmTierChange} className={styles.form}>
              <div className={styles.formGroup}>
                <label htmlFor="new-tier-select">Gói mới *</label>
                <select
                  id="new-tier-select"
                  value={selectedNewTier}
                  onChange={(e) => handleSelectNewTier(e.target.value)}
                  className={styles.formSelect}
                  required
                  disabled={!!tierSuccess}
                >
                  <option value="">-- Chọn gói --</option>
                  {['lite', 'standard', 'professional', 'enterprise']
                    .filter((t) => t !== tierTargetTenant.tier)
                    .map((t) => (
                      <option key={t} value={t}>{getTierLabel(t)}</option>
                    ))}
                </select>
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="tier-reason">Lý do thay đổi</label>
                <textarea
                  id="tier-reason"
                  value={tierReason}
                  onChange={(e) => setTierReason(e.target.value)}
                  rows={2}
                  className={styles.formTextarea}
                  placeholder="Tùy chọn — ghi chú lý do đổi gói cho nhật ký kiểm toán"
                  disabled={!!tierSuccess}
                />
              </div>

              {/* Downgrade impact preview (GET .../downgrade-preview) — BR-TENM-010 FR-006 */}
              {isDowngradeSelected && (
                <div
                  style={{
                    background: downgradePreview?.is_safe === false ? '#FEF2F2' : '#FFF7ED',
                    border: `1px solid ${downgradePreview?.is_safe === false ? '#FCA5A5' : '#F59E0B'}`,
                    borderRadius: '6px',
                    padding: '12px',
                    marginBottom: '4px',
                    fontSize: '13px',
                    color: '#7C2D12',
                  }}
                  data-testid="downgrade-preview"
                >
                  {isLoadingDowngradePreview ? (
                    <span>Đang tải xem trước hạ gói...</span>
                  ) : downgradePreview ? (
                    downgradePreview.is_safe ? (
                      <span>✅ An toàn để hạ xuống gói {downgradePreview.new_tier_display}.</span>
                    ) : (
                      <>
                        <strong>⚠ Không thể hạ gói:</strong>
                        <ul style={{ margin: '6px 0 0', paddingLeft: '18px' }}>
                          {downgradePreview.warnings.map((w, idx) => (
                            <li key={idx}>{w}</li>
                          ))}
                        </ul>
                        {downgradePreview.excess_resources.users.length > 0 && (
                          <div style={{ marginTop: '6px' }}>
                            <strong>Người dùng vượt giới hạn:</strong>
                            <ul style={{ margin: '4px 0 0', paddingLeft: '18px' }}>
                              {downgradePreview.excess_resources.users.map((u) => (
                                <li key={u.id}>{u.full_name || u.email} ({u.role})</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </>
                    )
                  ) : null}
                </div>
              )}

              <div className={styles.modalActions}>
                <button
                  type="button"
                  onClick={closeTierModal}
                  className={styles.cancelButton}
                  disabled={isSubmittingTier}
                >
                  Hủy
                </button>
                <button
                  type="submit"
                  disabled={
                    isSubmittingTier ||
                    !!tierSuccess ||
                    !selectedNewTier ||
                    (isDowngradeSelected &&
                      (isLoadingDowngradePreview || downgradePreview?.is_safe === false))
                  }
                  className={styles.primaryButton}
                >
                  {isSubmittingTier ? 'Đang xử lý...' : 'Xác nhận đổi gói'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TenantManagement;
